import { Injectable } from '@angular/core';
import { OfferPDF, Offers, OffersList, OfferSteps, WebserviceResponse} from "../interfaces";
import { HttpClient, HttpErrorResponse, HttpParams } from "@angular/common/http";
import { AlertService, IAlertType } from "./alert.service";
import { Observable, throwError } from "rxjs";
import { catchError, retry } from "rxjs/operators";
import { restBaseUrl } from "../utils/rest-url";

@Injectable({
  providedIn: 'root'
})
export class OffersStoreService {
  offers: Offers[] = [];

  private readonly endpoint = restBaseUrl('/offers');

  constructor(
      private http: HttpClient,
      private alertDispatcher: AlertService
  ) { }

  private handleError(error: HttpErrorResponse){
    let errorMessage = 'Unknown error!';
    if (error.error instanceof ErrorEvent) {
      // Client-side errors
      errorMessage = `Client-side error: ${error.error.message}`;
    } else {
      // Server-side errors
      errorMessage = `Server-side error code: ${error.status} - message: ${error.message}`;
    }
    // window.alert(errorMessage);

    this.alertDispatcher.show({
      message: errorMessage,
      clrAlertType: IAlertType.DANGER
    }, 5000);

    // return throwError(errorMessage); deprecated
    return throwError(() => new Error(errorMessage));
  }

  // Main CRUD Services

  public createOffer(prid: string) {
    return this.http.post(
        `${this.endpoint}/add_offers?project_id=${prid}`,
        { responseType: 'text'}
    ).pipe(
        retry(3),
        catchError(this.handleError.bind(this))
    );
  }

  public updateOfferTotal(projectID: string, offerID: number, total: number){
    return this.http.put(
        `${this.endpoint}/update_budget?project_id=${projectID}&offer_id=${offerID}&budget=${total}`,
        { responseType: 'text'}
    ).pipe(
        retry(3),
        catchError(this.handleError.bind(this))
    );
  }

  public getOffers(projectId: string, offerId: number, state: string): Observable<WebserviceResponse<Offers[]>> {
    const options = new HttpParams({
      fromObject: {
        project_number: projectId,
        offer_id: offerId,
        status: state
      }
    });
    return this.http.get<WebserviceResponse<Offers[]>>(this.endpoint + '/get_offers',
        { params: options }).pipe(
        retry(3),
        catchError(this.handleError.bind(this))
    );
  }

  public copyOffer(projectID: string, offerID: number):Observable<WebserviceResponse<any>>{
    return this.http.post<WebserviceResponse<any>>(
        `${this.endpoint}/copy_offer?project_id=${projectID}&offer_id=${offerID}`,
        { responseType: 'text'}
    ).pipe(
        retry(3),
        catchError(this.handleError.bind(this))
    );
  }

  public deleteOffer(projectID: string, offerID: number){
    return this.http.delete(
        `${this.endpoint}/delete_offers?project_number=${projectID}&offer_id=${offerID}`,
        { responseType: 'text'}
    ).pipe(
        retry(3),
        catchError(this.handleError.bind(this))
    );
  }

  public updateOffer(o: OfferSteps) {
    return this.http.put(
        `${this.endpoint}/update_offers`,
        o,
        { responseType: 'json'}
    ).pipe(
        retry(3),
        catchError(this.handleError.bind(this))
    );
  }

  public updatePDFValue(o: OfferPDF) {
    return this.http.put(
        `${this.endpoint}/update_pdf_value`,
        o,
        { responseType: 'json'}
    ).pipe(
        retry(3),
        catchError(this.handleError.bind(this))
    );
  }

  // Offer Position Services

  public getOffersPositions(projectID: string, offerID: number, positionID: string, partID: number): Observable<WebserviceResponse<OffersList[]>> {
    const options = new HttpParams({
      fromObject: {
        project_number: projectID,
        offer_id: offerID,
        position: positionID,
        part: partID
      }
    });
    return this.http.get<WebserviceResponse<OffersList[]>>(this.endpoint + '/get_offers_position',
        { params: options }).pipe(
        retry(3),
        catchError(this.handleError.bind(this))
    );
  }

  public updateOffersPosition(offer: OffersList) {
    return this.http.put(
        `${this.endpoint}/update_offers_position`,
        offer,
        { responseType: 'json'}
    ).pipe(
        retry(3),
        catchError(this.handleError.bind(this))
    );
  }

  public deleteOffersPosition(projectID: string, offerID: number, positionID: string, partID: number) {
    return this.http.delete(
        `${this.endpoint}/delete_offer_position?project_number=${projectID}&offer_id=${offerID}&position=${positionID}&part=${partID}`,
        { responseType: 'text'}
    ).pipe(
        retry(3),
        catchError(this.handleError.bind(this))
    );
  }

  // Offer Status

  public updateOfferStatus(projectID: string, offerID: string, status: string){
    return this.http.put(
        `${this.endpoint}/update_status?project_id=${projectID}&offer_id=${offerID}&status=${status}`,
        { responseType: 'text'}
    ).pipe(
        retry(3),
        catchError(this.handleError.bind(this))
    );
  }

  public addSingleProjectPosition(projectID: string, offerID: number, positionID: number, partID: number) {
    return this.http.post(
        `${this.endpoint}/add_single_offer_position?project_id=${projectID}&offer_id=${offerID}&part=${partID}&position=${positionID}`,
        { responseType: 'text'}
    ).pipe(
        retry(3),
        catchError(this.handleError.bind(this))
    );
  }
}
